import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { AccessRight } from '@stamhoofd/types/AccessRight';
import { Group } from './Group.js';
import { PermissionRole } from './PermissionRole.js';
import { PermissionsByRole } from './PermissionsByRole.js';
import { PermissionsResourceType } from '@stamhoofd/types/PermissionsResourceType';
/**
 * Give access to a given resouce based by the roles of a user
 */
export class GroupCategoryPermissions extends AutoEncoder {
    /**
     * @deprecated
     * Can create new groups in this category or subcategories
     */
    create = [];
    /**
     * @deprecated
     * Permissions automatically for all groups in this category
     */
    groupPermissions = PermissionsByRole.create({});
}
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRole) })
], GroupCategoryPermissions.prototype, "create", void 0);
__decorate([
    field({ decoder: PermissionsByRole, version: 203, optional: true })
], GroupCategoryPermissions.prototype, "groupPermissions", void 0);
export class GroupCategorySettings extends AutoEncoder {
    name = '';
    /**
     * Small text below the category title
     */
    description = '';
    public = true;
    maximumRegistrations = null;
    /**
     * @deprecated
     */
    permissions = GroupCategoryPermissions.create({});
    /**
     * Whether the category can be deleted / moved by non admins.
     */
    locked = false;
}
__decorate([
    field({ decoder: StringDecoder })
], GroupCategorySettings.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], GroupCategorySettings.prototype, "description", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], GroupCategorySettings.prototype, "public", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 59 })
], GroupCategorySettings.prototype, "maximumRegistrations", void 0);
__decorate([
    field({ decoder: GroupCategoryPermissions, version: 61 })
], GroupCategorySettings.prototype, "permissions", void 0);
__decorate([
    field({
        decoder: BooleanDecoder, version: 330,
    })
], GroupCategorySettings.prototype, "locked", void 0);
export class GroupCategory extends AutoEncoder {
    id;
    settings = GroupCategorySettings.create({});
    /**
     * We only save the ID of groups because these are stored separately. To prevent that a group will be deleted when we simply move the group to a different category
     */
    groupIds = [];
    /**
     * Child category IDS
     * We use ID's to allow more flexible changes in the future
     */
    categoryIds = [];
    /**
     * Returns all parent and grandparents of this group
     */
    getParentCategories(all) {
        const map = new Map();
        // Avoid recursive loop: can never call getParentCategories on itself again
        const filteredAll = all.filter(g => g.id !== this.id);
        const parents = filteredAll.filter(g => g.categoryIds.includes(this.id));
        for (const parent of parents) {
            map.set(parent.id, parent);
            const hisParents = parent.getParentCategories(filteredAll);
            for (const pp of hisParents) {
                map.set(pp.id, pp);
            }
        }
        return [...map.values()];
    }
    isPublic(allCategories) {
        if (!this.settings.public) {
            return false;
        }
        for (const parent of this.getParentCategories(allCategories)) {
            if (!parent.settings.public) {
                return false;
            }
        }
        return true;
    }
    canEdit(permissions) {
        if (permissions?.hasFullAccess()) {
            return true;
        }
        return false;
    }
    canCreate(permissions, categories = []) {
        if (!permissions) {
            return false;
        }
        if (permissions.hasResourceAccessRight(PermissionsResourceType.GroupCategories, this.id, AccessRight.OrganizationCreateGroups)) {
            return true;
        }
        const parents = this.getParentCategories(categories);
        for (const parent of parents) {
            if (permissions.hasResourceAccessRight(PermissionsResourceType.GroupCategories, parent.id, AccessRight.OrganizationCreateGroups)) {
                return true;
            }
        }
        return false;
    }
    getDiffName() {
        return this.settings.name;
    }
    isRoot(period) {
        return this.id === period.settings.rootCategoryId;
    }
    /**
     * Returns the display name of this category. The root category has no name of its own,
     * so we return a sensible default based on whether it holds subcategories or only groups.
     */
    getName(period) {
        if (this.isRoot(period) && !this.settings.name) {
            return this.categoryIds.length > 0 ? $t('%1cO') : $t('%P4');
        }
        return this.settings.name || $t('%CL');
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], GroupCategory.prototype, "id", void 0);
__decorate([
    field({ decoder: GroupCategorySettings })
], GroupCategory.prototype, "settings", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], GroupCategory.prototype, "groupIds", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], GroupCategory.prototype, "categoryIds", void 0);
export class GroupCategoryTree extends GroupCategory {
    groups = [];
    categories = [];
    get depth() {
        if (this.groups.length > 0) {
            return 0;
        }
        if (this.categories.length == 0) {
            return 0;
        }
        return Math.max(...this.categories.map(c => c.depth)) + 1;
    }
    getAllGroups() {
        return [...this.groups, ...this.categories.flatMap(c => c.getAllGroups())];
    }
    getAllCategories() {
        return [...this.categories, ...this.categories.flatMap(c => c.getAllCategories())];
    }
    getMemberCount() {
        if (this.getAllGroups().length === 0) {
            return null;
        }
        let count = 0;
        for (const group of this.getAllGroups()) {
            const c = group.getMemberCount();
            if (c === null) {
                return null;
            }
            count += c;
        }
        return count;
    }
    static build(root, organizationPeriod, options = {}) {
        const categories = organizationPeriod.settings.categories;
        const groups = options?.groups ?? organizationPeriod.groups;
        const permissions = options.permissions ?? null;
        const maxDepth = options.maxDepth ?? null;
        const smartCombine = options.smartCombine ?? false;
        return GroupCategoryTree.create({
            ...root,
            categories: root.categoryIds.flatMap((id) => {
                const f = categories.find(c => c.id === id);
                if (f) {
                    const t = GroupCategoryTree.build(f, organizationPeriod, {
                        ...options,
                        maxDepth: maxDepth !== null ? maxDepth - 1 : null,
                    });
                    if (t.categories.length == 0 && t.groups.length == 0 && (smartCombine || (permissions !== null && !permissions.hasResourceAccessRight(PermissionsResourceType.GroupCategories, t.id, AccessRight.OrganizationCreateGroups)))) {
                        // Hide empty categories where we cannot create new groups or when smart combine is enabled
                        return [];
                    }
                    // if (smartCombine && !t.categories.find(c => c.categories.length || c.groups.length > 1)) {
                    //     // If all categories only have groups and no more than 1 group, combine them all
                    //     t.groups = t.getAllGroups()
                    //     t.categories = []
                    // }
                    if (maxDepth !== null && t.depth >= maxDepth && t.categories.length > 0) {
                        const categories = [];
                        for (const cat of t.categories) {
                            if (smartCombine && cat.groups.length === 0 && cat.categories.length === 0) {
                                continue;
                            }
                            // Clone reference
                            cat.settings = GroupCategorySettings.create(cat.settings);
                            cat.settings.name = t.settings.name + ' - ' + cat.settings.name;
                            cat.settings.public = t.settings.public && cat.settings.public;
                            categories.push(cat);
                        }
                        // Concat here
                        return categories;
                    }
                    return [t];
                }
                return [];
            }),
            groups: root.groupIds.flatMap((id) => {
                const g = groups.find(c => c.id === id);
                if (g) {
                    // Hide groups we don't have permissions for
                    if (permissions && (!g.hasReadAccess(permissions, categories) && !g.settings.implicitlyAllowViewRegistrations)) {
                        return [];
                    }
                    return [g];
                }
                return [];
            }),
        });
    }
    /**
     * Filter groups
     */
    filter(keep) {
        const categories = this.categories.flatMap((category) => {
            const filtered = category.filter(keep);
            if (filtered.groups.length == 0 && filtered.categories.length == 0) {
                return [];
            }
            return [filtered];
        });
        const groups = this.groups.filter(keep);
        return GroupCategoryTree.create(Object.assign({}, this, {
            categories,
            groups,
        }));
    }
    /**
     * Remove empty categories and non-public categories
     * @param admin Whether not-public categories should be visible
     */
    filterForDisplay(admin = false, smartCombine = false) {
        const categories = this.categories.flatMap((category) => {
            if (!admin && !category.settings.public) {
                return [];
            }
            const filtered = category.filterForDisplay(admin);
            if (smartCombine && filtered.groups.length == 0 && filtered.categories.length == 0) {
                return [];
            }
            return [filtered];
        });
        return GroupCategoryTree.create(Object.assign({}, this, {
            categories,
        }));
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(Group) })
], GroupCategoryTree.prototype, "groups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(GroupCategoryTree) })
], GroupCategoryTree.prototype, "categories", void 0);
//# sourceMappingURL=GroupCategory.js.map